import { LayoutGrid, List, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useApp } from '@/context/AppContext';
import { openRouter } from '@/services/openRouter';
import { cn } from '@/utils/cn';
import type { ViewMode } from '@/types';

// ============================================
// HEADER COMPONENT
// Dashboard-specific toolbar: AI toggle + view toggle.
// Global nav (logo, tabs, sign-out) is handled by AppShell.
// ============================================

export function Header() {
  const { view, setView, openModal, toggleAIPanel, isAIPanelOpen } = useApp();

  const isAIConfigured = openRouter.isConfigured();

  const handleAIClick = () => {
    if (isAIConfigured) {
      toggleAIPanel();
    } else {
      openModal({ mode: 'settings' });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 pb-1 flex items-center justify-end gap-2">
      {/* AI Toggle */}
      <Button
        variant={isAIPanelOpen ? 'default' : 'ghost'}
        size="icon"
        onClick={handleAIClick}
        aria-label={
          !isAIConfigured
            ? 'Configure AI assistant'
            : isAIPanelOpen
              ? 'Close AI assistant'
              : 'Open AI assistant'
        }
        className={cn('relative', !isAIConfigured && 'opacity-50')}
        title={!isAIConfigured ? 'Click to configure API key' : undefined}
      >
        <Sparkles className="h-5 w-5" />
      </Button>

      {/* View Toggle */}
      <ViewToggle view={view} onViewChange={setView} />
    </div>
  );
}

// ============================================
// VIEW TOGGLE
// ============================================

interface ViewToggleProps {
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
}

function ViewToggle({ view, onViewChange }: ViewToggleProps) {
  return (
    <div className="flex items-center border border-[var(--border-primary)] rounded-lg p-0.5">
      <button
        type="button"
        onClick={() => onViewChange('grid')}
        className={cn(
          'p-1.5 rounded-md transition-colors',
          view === 'grid'
            ? 'bg-brand-100 text-brand-700'
            : 'text-[var(--text-tertiary)] hover:text-[var(--text-primary)]'
        )}
        aria-label="Grid view"
        aria-pressed={view === 'grid'}
      >
        <LayoutGrid className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => onViewChange('list')}
        className={cn(
          'p-1.5 rounded-md transition-colors',
          view === 'list'
            ? 'bg-brand-100 text-brand-700'
            : 'text-[var(--text-tertiary)] hover:text-[var(--text-primary)]'
        )}
        aria-label="List view"
        aria-pressed={view === 'list'}
      >
        <List className="h-4 w-4" />
      </button>
    </div>
  );
}
